import { PUBLIC_API_URL } from '@/lib/public-api-url';
import type { SeoRedirectResult, SeoResolveResult } from '../types';

// Client-side fetch to /api/v1/seo/redirect
// Returns discriminated result: redirect | not_found | error
export const getSeoRedirectReq = async (
  path: string,
  signal?: AbortSignal,
): Promise<SeoRedirectResult> => {
  try {
    const url = `${PUBLIC_API_URL}/api/v1/seo/redirect?path=${encodeURIComponent(path)}`;
    const response = await fetch(url, { signal });

    if (response.status === 404) {
      return { status: 'not_found' };
    }

    if (!response.ok) {
      return { status: 'error', error: new Error(`Unexpected status ${response.status}`) };
    }

    const data = await response.json();
    return { status: 'redirect', data };
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') {
      throw error;
    }
    return { status: 'error', error };
  }
};

interface SeoResolveParams {
  path?: string;
  pageKey?: string;
}

// Client-side fetch to /api/v1/seo/resolve
// Accepts either `path` (permalink) or `pageKey` (from a previous /redirect response)
export const getSeoResolveReq = async (
  params: SeoResolveParams,
  signal?: AbortSignal,
): Promise<SeoResolveResult> => {
  try {
    const queryParams = new URLSearchParams();
    if (params.pageKey) queryParams.set('pageKey', params.pageKey);
    else if (params.path) queryParams.set('path', params.path);

    const url = `${PUBLIC_API_URL}/api/v1/seo/resolve?${queryParams.toString()}`;
    const response = await fetch(url, { signal });

    if (response.status === 404) {
      return { status: 'not_found' };
    }

    if (!response.ok) {
      return { status: 'error', error: new Error(`Unexpected status ${response.status}`) };
    }

    const data = await response.json();
    return { status: 'ok', data };
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') {
      throw error;
    }
    return { status: 'error', error };
  }
};
